/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { Component, type ReactElement, type ErrorInfo, useContext } from 'react';

import { jsx } from '@compiled/react';

import { OPERATIONAL_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import SectionMessage from '@atlaskit/section-message/message';
import { type ForgeUIExtensionType, type ProductEnvironment } from '@atlassian/forge-ui-types';

import { FORGE_UI_ANALYTICS_CHANNEL } from '../analytics';
import { EnvironmentContext, useMetricsContext } from '../context';
import { captureAndReportError } from '../error-reporting';
import { APIError } from '../web-client/errors';

interface Props {
	children: ReactElement;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	environment: ProductEnvironment;
	extension?: ForgeUIExtensionType;
	page: string;
}

interface State {
	thrownError?: {
		error: Error;
		errorInfo: ErrorInfo;
	};
}
class ForgeErrorBoundary extends Component<Props, State> {
	public state: State = {
		thrownError: undefined,
	};

	public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		this.setState({
			thrownError: {
				error,
				errorInfo,
			},
		});
		const { page } = this.props;
		const environment = this.props.environment;

		captureAndReportError({
			error,
			errorExtensionDetails: this.props.extension
				? {
						type: this.props.extension.type,
						environmentType: this.props.extension.environmentType,
						appOwnerAccountId: this.props.extension.appOwner?.accountId,
						properties: this.props.extension.properties,
						id: this.props.extension?.id,
					}
				: undefined,
			errorInfo,
			page,
			environment,
		});

		if (!(error instanceof APIError)) {
			this.props
				.createAnalyticsEvent({
					eventType: OPERATIONAL_EVENT_TYPE,
					data: {
						action: 'failed',
						actionSubject: 'forge.ui.renderer',
						source: page,
						tags: ['forge'],
						attributes: {
							target: 'errorBoundary',
							errorName: error.name,
						},
					},
				})
				.fire(FORGE_UI_ANALYTICS_CHANNEL);
		}
	}

	public render() {
		const { thrownError } = this.state;
		if (!thrownError) {
			return this.props.children;
		}
		return (
			<div data-testid="ForgeErrorBoundary">
				<SectionMessage appearance="error" title="Error rendering app">
					{thrownError.error.message}
				</SectionMessage>
			</div>
		);
	}
}

const withAnalyticsContext =
	(Component: typeof ForgeErrorBoundary) =>
	({
		extension,
		children,
	}: Omit<Props, 'page' | 'environment' | 'createAnalyticsEvent'> & {
		children?: React.ReactNode;
	}): JSX.Element => {
		const { page, environment: envFromContext } = useMetricsContext();
		const environment = useContext(EnvironmentContext);
		const { createAnalyticsEvent } = useAnalyticsEvents();

		return (
			<Component
				environment={envFromContext ?? environment}
				extension={extension}
				page={page}
				createAnalyticsEvent={createAnalyticsEvent}
			>
				{children}
			</Component>
		);
	};

const _default_1: ({
	extension,
	children,
}: Omit<Props, 'page' | 'environment' | 'createAnalyticsEvent'> & {
	children?: React.ReactNode;
}) => JSX.Element = withAnalyticsContext(ForgeErrorBoundary);
export default _default_1;
